import * as React from 'react';


import './MainFrame.css';
import Container from 'react-bootstrap/Container';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Toast from 'react-bootstrap/Toast';
import ToastContainer from 'react-bootstrap/ToastContainer';
import {MdPersonRemove} from "react-icons/md";
import {Route, Routes} from "react-router-dom";

import type {
    BaseComponentProps,
    BaseComponentState,
} from "./base/ui_general/BaseComponent";
import {
    BaseComponent,
    BaseComponentStateInitial
} from "./base/ui_general/BaseComponent";
import type {NavigateFunction} from "react-router-dom";
import {
    getLabelTextMessage,
    getQueryString,
    getUserLanguage,
    setUserLanguage
} from "./base/utils/GeneralUtilities";
import type {
    CashReserve, CashTranche,
    FeedbackMessage,
    FeedbackMessageDispatch, Formula, Portfolio,
    Transaction, Trigger,
    UUID,
    Variable, Waterfall, WaterfallItem, TimeSeries, Fee,
    SyntheticTranche, SyntheticExcessSpread, Replenishment, TransactionSettings,
    PortfolioStatistics,
    WaterfallItemConcept,
    ChatHistory
} from "./base/ui_general/GeneralInterfaces";
import autobind from "autobind-decorator";
import {
    ApiEndPoints,
    FeedbackMessageDisplayTimes,
    FeedbackMessageTypes,
    FrontendURLPaths,
    LabelTextMessageTypes,
    UserLanguages,
} from "./base/Settings";
import {LandingPageOverView} from "./landing_page/LandingPageOverView";
import {DesignCenterOverView} from "./designcenter/DesignCenterOverView";
import {getRequest_get_all_transactions} from "./api/transaction.tsx";
import {getRequest_get_all_variables} from "./api/variable";
import {getRequest_get_all_waterfall_item} from "./api/waterfall_item";
import {getRequest_get_all_formulas} from "./api/formula";
import {getRequest_get_all_portfolios} from "./api/portfolio";
import {getRequest_get_all_triggers} from "./api/trigger";
import {getRequest_get_all_cash_reserves} from "./api/cash_reserve";
import {getRequest_get_all_cash_tranches} from "./api/cash_tranche";
import {getRequest_get_all_time_series} from "./api/time_series";
import {getRequest_get_all_fees} from "./api/fee";
import {getRequest_get_all_waterfalls} from "./api/waterfall.tsx";
import {getRequest_get_all_synthetic_tranches} from "./api/synthetic_tranche";
import {getRequest_get_all_synthetic_excess_spreads} from "./api/synthetic_excess_spread";
import {getRequest_get_all_replenishments} from "./api/replenishment";
import {getRequest_get_transaction_settings} from "./api/transaction_settings.tsx";
import {getRequest_get_portfolio_statistics} from "./api/portfolio_statistics";
import {getRequest_get_waterfall_item_concepts} from "./api/waterfall_item_concept";
import {logout, isAuthenticated} from './auth/KeycloakAuth';
import {getRequest_get_chat} from "./api/chat.tsx";

/**
 * The properties of this Main Frame require a navigate function from the react-router-dom to allow navigating
 * on the site.
 */
interface MainFrameProps extends BaseComponentProps {
    /** Allows navigating within the react-router-dom without reloading the website. */
    navigate: NavigateFunction;
}


/**
 * MainFrameState defines the shape of the state object for the MainFrame component.
 * It includes user language, feedback messages, transaction info, window size, and all data states
 * required for the DesignCenterOverView, such as variables, waterfall items, formulas, triggers, portfolios,
 * cash reserves, and cash tranches.
 */
interface MainFrameState extends BaseComponentState {
    /** Indicates the preferred user language. This state variable reflects the user language setting in the local
     * memory. All textual user feedback shall be provided in this indicated language. When changing this language
     * setting, all system specific texts displayed on screen shall be adapted. */
    user_language: UserLanguages;
    /** This list holds feedback messages that are to be displayed on screen */
    message_dispatch_list: Array<FeedbackMessageDispatch>;
    /** Currently selected transaction, if any */
    transaction: Transaction | undefined;
    /** Stores the current inner height of the browser window for responsive layout calculations. */
    windowInnerHeight: number;
    /** Array of variables loaded for the current transaction. */
    variables: Array<Variable> | undefined;
    /** Array of waterfall items loaded for the current transaction. */
    waterfall_items: Array<WaterfallItem> | undefined;
    /** Array of time series loaded for the current transaction. */
    time_series: Array<TimeSeries> | undefined;
    /** Array of fees loaded for the current transaction. */
    fees: Array<Fee> | undefined;
    /** Array of waterfalls loaded for the current transaction. */
    waterfalls: Array<Waterfall> | undefined;
    /** Array of formulas loaded for the current transaction. */
    formulas: Array<Formula> | undefined;
    /** Array of triggers loaded for the current transaction. */
    triggers: Array<Trigger> | undefined;
    /** Array of portfolios loaded for the current transaction. */
    portfolios: Array<Portfolio> | undefined;
    /** Array of cash reserves loaded for the current transaction. */
    cash_reserves: Array<CashReserve> | undefined;
    /** Array of cash tranches loaded for the current transaction. */
    cash_tranches: Array<CashTranche> | undefined;
    /** Array of synthetic tranches loaded for the current transaction. */
    synthetic_tranches: Array<SyntheticTranche> | undefined;
    /** Array of synthetic excess spreads loaded for the current transaction. */
    synthetic_excess_spreads: Array<SyntheticExcessSpread> | undefined;
    /** Array of replenishments loaded for the current transaction. */
    replenishments: Array<Replenishment> | undefined;
    /** Transaction settings loaded for the current transaction. */
    transaction_settings: TransactionSettings | undefined;
    /** Array of portfolio statistics loaded for the current transaction. */
    portfolio_statistics: Array<PortfolioStatistics> | undefined;
    /** Concepts for waterfall items loaded for the current transaction. */
    waterfall_item_concepts: WaterfallItemConcept | undefined;
    /** Chat transcript loaded from the server for the current user/thread. */
    chat_history: ChatHistory | undefined;
}


export class MainFrame extends BaseComponent<MainFrameProps, MainFrameState> {
    /**
     * Initializes the state and the input state controller for the login.
     * @param props React properties of this component
     */
    constructor(props: MainFrameProps) {
        super(props);

        this.state = {
            ...BaseComponentStateInitial,
            transaction: undefined,
            user_language: getUserLanguage(),
            message_dispatch_list: [],
            windowInnerHeight: window.innerHeight,
            variables: undefined,
            waterfall_items: undefined,
            time_series: undefined,
            fees: undefined,
            waterfalls: undefined,
            formulas: undefined,
            triggers: undefined,
            portfolios: undefined,
            cash_reserves: undefined,
            cash_tranches: undefined,
            synthetic_tranches: undefined,
            synthetic_excess_spreads: undefined,
            replenishments: undefined,
            transaction_settings: undefined,
            portfolio_statistics: undefined,
            waterfall_item_concepts: undefined,
            chat_history: undefined,
        };
    }


    /**
     * Is called from React after this component has been created, rendered and mounted to the DOM tree. Extracts the
     * base token from the browser's URL field (if any) and saves it as cookie in the browser's cookie storage. Also,
     * sets the callback to receive validity updates of the data entered into the login form.
     */
    componentDidMount() {
        window.addEventListener('resize', this.updateWindowSizes);
    }


    /**
     * Is called from React immediately before component will be destroyed and unmounted from the DOM tree.
     */
    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowSizes);
    }


    /**
     * Updates the innerHeight state property to reflect the current browser window height.
     * This is used for responsive layout calculations when the window is resized.
     */
    @autobind
    updateWindowSizes() {
        this.setState({ windowInnerHeight: window.innerHeight });
    }


    /**
     * This function removes all feedback message from the feedback message dispatch list which are older than the
     * corresponding indicated display-until timestamp.
     */
    removeExpiredFeedbackMessages() {
        const current_time = new Date();
        if (this.state.message_dispatch_list.filter(
            msg => msg.display_until_timeStamp < current_time
        ).length > 0) {
            this.setState(state => {
                return {
                    message_dispatch_list: state.message_dispatch_list.filter(
                        msg => msg.display_until_timeStamp >= current_time
                    )
                }
            })
        }
    }


    /**
     * This function is used as a callback function distributed everywhere in the system to allow the display of user
     * feedback on screen. It adds the provided feedback message to the feedback message dispatch list.
     * @param feedback_message Feedback message to be displayed on screen.
     */
    @autobind
    displayMessage(feedback_message: FeedbackMessage) {
        const current_time = new Date();
        const type_key = Object.keys(FeedbackMessageDisplayTimes).find(key => {
            return FeedbackMessageTypes[key as keyof typeof FeedbackMessageTypes] === feedback_message.message_type
        })
        if (type_key) {
            const display_time = FeedbackMessageDisplayTimes[
                type_key as keyof typeof FeedbackMessageDisplayTimes];

            this.setState(state => {
                return {
                    message_dispatch_list: [
                        ...state.message_dispatch_list,
                        {
                            ref: 'Feedback-Message [' + current_time + ']: ' + feedback_message.message_text,
                            feedback_message: feedback_message,
                            dispatch_timestamp: current_time,
                            display_time: display_time,
                            display_until_timeStamp: new Date(current_time.getTime() + 1000 * display_time)
                        }]
                }
            })
        }
    }


    /**
     * Callback function passed to all views. Allows receiving transaction id resolved by individual
     * view from query string parameters and possibly further API calls for resolution purposes.
     * @param transaction_id UUID of the currently selected model
     */
    @autobind
    provideModelIDs(transaction_id: UUID | undefined) {
        if (transaction_id) {
            this.fetchTransaction(transaction_id);
        }
    }


    /**
     * Fetches all variables for the current transaction. If no transaction is selected, clears the variables state.
     */
    @autobind
    fetchVariables() {
        if (!this.state.transaction) {
            this.setState({ variables: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_variables(this.state.transaction.id),
            (variables: Array<Variable>) => {
                this.setState({ variables: variables });
            }
        );
    }

    /**
     * Fetches all waterfall items for the current transaction. If no transaction is selected, clears the waterfall_items state.
     */
    @autobind
    fetchWaterfallItems() {
        if (!this.state.transaction) {
            this.setState({ waterfall_items: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_waterfall_item(this.state.transaction.id),
            (waterfall_items: Array<WaterfallItem>) => {
                this.setState({ waterfall_items: waterfall_items });
            }
        );
    }

    /**
     * Fetches all time series for the current transaction. If no transaction is selected, clears the time_series state.
     */
    @autobind
    fetchTimeSeries() {
        if (!this.state.transaction) {
            this.setState({ time_series: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_time_series(this.state.transaction.id),
            (time_series: Array<TimeSeries>) => {
                this.setState({ time_series: time_series });
            }
        );
    }

    /**
     * Fetches all fees for the current transaction. If no transaction is selected, clears the fees state.
     */
    @autobind
    fetchFees() {
        if (!this.state.transaction) {
            this.setState({ fees: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_fees(this.state.transaction.id),
            (fees: Array<Fee>) => {
                this.setState({ fees: fees });
            }
        );
    }

    /**
     * Fetches all waterfalls for the current transaction. If no transaction is selected, clears the waterfalls state.
     */
    @autobind
    fetchWaterfalls() {
        if (!this.state.transaction) {
            this.setState({ waterfalls: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_waterfalls(this.state.transaction.id),
            (waterfalls: Array<Waterfall>) => {
                this.setState({ waterfalls: waterfalls });
            }
        );
    }

    /**
     * Fetches all formulas for the current transaction. If no transaction is selected, clears the formulas state.
     */
    @autobind
    fetchFormulas() {
        if (!this.state.transaction) {
            this.setState({ formulas: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_formulas(this.state.transaction.id),
            (formulas: Array<Formula>) => {
                this.setState({ formulas: formulas });
            }
        );
    }

    /**
     * Fetches all portfolios for the current transaction. If no transaction is selected, clears the portfolios state.
     */
    @autobind
    fetchPortfolios() {
        if (!this.state.transaction) {
            this.setState({ portfolios: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_portfolios(this.state.transaction.id),
            (portfolios: Array<Portfolio>) => {
                this.setState({ portfolios: portfolios });
            }
        );
    }

    /**
     * Fetches all triggers for the current transaction. If no transaction is selected, clears the triggers state.
     */
    @autobind
    fetchTriggers() {
        if (!this.state.transaction) {
            this.setState({ triggers: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_triggers(this.state.transaction.id),
            (triggers: Array<Trigger>) => {
                this.setState({ triggers: triggers });
            }
        );
    }

    /**
     * Fetches all cash reserves for the current transaction. If no transaction is selected, clears the cash_reserves state.
     */
    @autobind
    fetchCashReserves() {
        if (!this.state.transaction) {
            this.setState({ cash_reserves: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_cash_reserves(this.state.transaction.id),
            (cash_reserves: Array<CashReserve>) => {
                this.setState({ cash_reserves: cash_reserves });
            }
        );
    }

    /**
     * Fetches all cash tranches for the current transaction. If no transaction is selected, clears the cash_tranches state.
     */
    @autobind
    fetchCashTranches() {
        if (!this.state.transaction) {
            this.setState({ cash_tranches: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_cash_tranches(this.state.transaction.id),
            (cash_tranches: Array<CashTranche>) => {
                this.setState({ cash_tranches: cash_tranches });
            }
        );
    }

    /**
     * Fetches all synthetic tranches for the current transaction. If no transaction is selected, clears the synthetic_tranches state.
     */
    @autobind
    fetchSyntheticTranches() {
        if (!this.state.transaction) {
            this.setState({ synthetic_tranches: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_synthetic_tranches(this.state.transaction.id),
            (synthetic_tranches: Array<SyntheticTranche>) => {
                this.setState({ synthetic_tranches: synthetic_tranches });
            }
        );
    }

    /**
     * Fetches all synthetic excess spreads for the current transaction. If no transaction is selected, clears the synthetic_excess_spreads state.
     */
    @autobind
    fetchSyntheticExcessSpreads() {
        if (!this.state.transaction) {
            this.setState({ synthetic_excess_spreads: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_synthetic_excess_spreads(this.state.transaction.id),
            (synthetic_excess_spreads: Array<SyntheticExcessSpread>) => {
                this.setState({ synthetic_excess_spreads: synthetic_excess_spreads });
            }
        );
    }

    /**
     * Fetches all replenishments for the current transaction. If no transaction is selected, clears the replenishments state.
     */
    @autobind
    fetchReplenishments() {
        if (!this.state.transaction) {
            this.setState({ replenishments: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_replenishments(this.state.transaction.id),
            (replenishments: Array<Replenishment>) => {
                this.setState({ replenishments: replenishments });
            }
        );
    }

    /**
     * Fetches the transaction for the given transaction_id, or reloads the transaction in state if no id is provided.
     * If no transaction is found, clears the transaction state and displays a feedback message.
     * @param transaction_id Optional UUID of the transaction to load. If not provided, reloads the transaction in state.
     */
    @autobind
    fetchTransaction(transaction_id?: UUID) {
        const idToFetch = transaction_id ?? this.state.transaction?.id;
        if (!idToFetch) {
            this.setState({ transaction: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_all_transactions(idToFetch),
            (transactions: Array<Transaction>) => {
                if (transactions.length > 0) {
                    this.setState({ transaction: transactions[0] });
                } else {
                    this.setState({ transaction: undefined });
                    const feedback_message: FeedbackMessage = {
                        message_type: FeedbackMessageTypes.ERROR,
                        message_text: "Transaction not found for id: " + idToFetch
                    };
                    this.displayMessage(feedback_message);
                }
            }
        );
    }


    /**
     * Fetches the transaction settings for the current transaction. If no transaction is selected, clears the transaction_settings state.
     */
    @autobind
    fetchTransactionSettings() {
        if (!this.state.transaction) {
            this.setState({ transaction_settings: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_transaction_settings(this.state.transaction.id),
            (settings: TransactionSettings) => {
                this.setState({ transaction_settings: settings });
            }
        );
    }

    /**
     * Fetches portfolio statistics for the current transaction. If no transaction is selected, clears the portfolio_statistics state.
     */
    @autobind
    fetchPortfolioStatistics() {
        if (!this.state.transaction) {
            this.setState({ portfolio_statistics: undefined });
            return;
        }
        this.processFetchRequest(
            getRequest_get_portfolio_statistics(this.state.transaction.id),
            (portfolio_statistics: Array<PortfolioStatistics>) => {
                this.setState({ portfolio_statistics: portfolio_statistics });
            }
        );
    }

    /**
     * Fetches concepts for waterfall items.
     */
    @autobind
    fetchWaterfallItemConcepts() {
        this.processFetchRequest(
            getRequest_get_waterfall_item_concepts(),
            (concepts: WaterfallItemConcept) => {
                this.setState({ waterfall_item_concepts: concepts });
            }
        );
    }

    /**
     * Fetches the chat history for the current user/thread. If a transaction is selected, passes its id.
     */
    @autobind
    fetchChatHistory() {
        this.processFetchRequest(
            getRequest_get_chat(this.state.transaction?.id),
            (output: ChatHistory) => {
                this.setState({ chat_history: output });
            }
        );
    }

    /**
     * Loads data for the specified endpoints. If no endpoints are provided, loads all relevant data for the current transaction.
     * This function is passed as a prop to views such as DesignCenterOverView, allowing them to trigger batch data loading.
     *
     * @param endpoints Optional array of endpoint types to fetch. If omitted, all supported endpoints are loaded.
     * Each endpoint corresponds to a specific entity in the transaction model (see ApiEndPoints).
     */
    @autobind
    loadData(endpoints?: Array<ApiEndPoints>) {
        const fetchMap: Partial<Record<ApiEndPoints, () => void>> = {
            [ApiEndPoints.TRANSACTION]: this.fetchTransaction,
            [ApiEndPoints.VARIABLE]: this.fetchVariables,
            [ApiEndPoints.WATERFALL_ITEM]: this.fetchWaterfallItems,
            [ApiEndPoints.TIME_SERIES]: this.fetchTimeSeries,
            [ApiEndPoints.FEE]: this.fetchFees,
            [ApiEndPoints.FORMULA]: this.fetchFormulas,
            [ApiEndPoints.PORTFOLIO]: this.fetchPortfolios,
            [ApiEndPoints.TRIGGER]: this.fetchTriggers,
            [ApiEndPoints.CASH_RESERVE]: this.fetchCashReserves,
            [ApiEndPoints.CASH_TRANCHE]: this.fetchCashTranches,
            [ApiEndPoints.SYNTHETIC_TRANCHE]: this.fetchSyntheticTranches,
            [ApiEndPoints.SYNTHETIC_EXCESS_SPREAD]: this.fetchSyntheticExcessSpreads,
            [ApiEndPoints.REPLENISHMENT]: this.fetchReplenishments,
            [ApiEndPoints.WATERFALL]: this.fetchWaterfalls,
            [ApiEndPoints.TRANSACTION_SETTINGS]: this.fetchTransactionSettings,
            [ApiEndPoints.PORTFOLIO_STATISTICS]: this.fetchPortfolioStatistics,
            [ApiEndPoints.WATERFALL_ITEM_CONCEPTS]: this.fetchWaterfallItemConcepts,
            [ApiEndPoints.CHAT]: this.fetchChatHistory,
        };
        if (!endpoints) {
            Object.values(fetchMap).forEach(fn => fn && fn());
        } else {
            Array.from(new Set(endpoints)).forEach(endpoint => {
                const fn = fetchMap[endpoint];
                if (fn) fn();
            });
        }
    };


    /**
     * Renders a toast container holding toasts for each feedback message in the feedback message dispatch list.
     * @returns An React.JSX.Element providing the toast container holding the feedback messages.
     */
    renderFeedbackMessageToasts(): React.JSX.Element {
        const current_time = new Date();

        return (
            <ToastContainer
                className="p-3"
                position='bottom-center'
                style={{ zIndex: 1 }}
            >
                {this.state.message_dispatch_list.map(msg_disp => (
                    <Toast
                        key={msg_disp.ref}
                        onClose={() => {
                            this.setState(state => {
                                return {
                                    message_dispatch_list: state.message_dispatch_list.filter(
                                        msg => msg.ref !== msg_disp.ref
                                    )
                                }
                            })
                        }}
                        show={true}
                        delay={msg_disp.display_time * 1000}
                        autohide
                        bg={msg_disp.feedback_message.message_type === FeedbackMessageTypes.ERROR ? 'danger' :
                            (msg_disp.feedback_message.message_type === FeedbackMessageTypes.WARNING ? 'warning' :
                                (msg_disp.feedback_message.message_type === FeedbackMessageTypes.SUCCESS ? 'success' :
                                    'info'))}
                    >
                        <Toast.Header>
                            <strong className="me-auto">{msg_disp.feedback_message.message_type}</strong>
                            <small>
                                {
                                    current_time.getTime() - msg_disp.dispatch_timestamp.getTime() < 1000 ? 'Now' :
                                        Math.round(
                                            (current_time.getTime() - msg_disp.dispatch_timestamp.getTime()) / 1000
                                        ) + ' seconds ago'
                                }
                            </small>
                        </Toast.Header>
                        <Toast.Body className={'text-white'}>{msg_disp.feedback_message.message_text}</Toast.Body>
                    </Toast>
                ))}
            </ToastContainer>
        )
    }


    /**
     * Renders the header bar of the main frame.
     * @returns An React.JSX.Element providing the header bar.
     */
    renderHeaderBar(): React.JSX.Element {
        const { transaction } = this.state;
        return (
            <Navbar
                fixed="top"
                className="mainframe-navbar w-100"
                style={{
                    backgroundColor: "#EFEEEB",
                    marginLeft: 0,
                    marginRight: 0,
                    borderRadius: 0
                }}
            >
                <Container fluid className="p-0 m-0 mainframe-header-flex"
                           style={{display: 'flex', alignItems: 'center', position: 'relative'}}>
                    <Navbar.Brand href="https://www.d-fine.com/"
                                  style={{marginRight: 16, display: 'flex', alignItems: 'center'}}>
                        <img
                            alt=""
                            src="/d-fine_logo.png"
                            width="100"
                            height="30"
                            className="d-inline-block align-top"
                            style={{display: 'block', marginTop: '8px'}} // moved image further down
                        />
                    </Navbar.Brand>
                    <div style={{display: 'flex', alignItems: 'center', height: '100%'}}>
                        <h2 style={{
                            margin: 0,
                            color: '#003C50',
                            fontSize: '2rem',
                            fontWeight: 400,
                            lineHeight: 1
                        }}>Securitization Design Suite</h2>
                    </div>
                    <Navbar.Toggle aria-controls="navbarScroll"/>
                    <Navbar.Collapse id="navbarScroll" style={{paddingTop: 12, paddingLeft: 20, flex: 1}}>
                        <Nav
                            className="me-auto my-2 my-lg-0"
                            style={{maxHeight: '100px'}}
                            navbarScroll
                        >
                            <Nav.Link href="#action1" onClick={() => {
                                this.props.navigate(FrontendURLPaths.LANDING_PAGE, {replace: false});
                            }}>
                                {getLabelTextMessage(LabelTextMessageTypes.HOME, this.state.user_language)}
                            </Nav.Link>
                            <NavDropdown
                                title={getLabelTextMessage(LabelTextMessageTypes.LANGUAGE, this.state.user_language)}
                                id="navbarScrollingDropdown"
                            >
                                {/* English */}
                                <NavDropdown.Item href="#action3" onClick={() => {
                                    setUserLanguage(UserLanguages.EN);
                                    this.setState({user_language: getUserLanguage()})
                                }
                                }>
                                    {getLabelTextMessage(LabelTextMessageTypes.ENGLISH, this.state.user_language)}
                                </NavDropdown.Item>

                                {/* German */}
                                <NavDropdown.Item href="#action3" onClick={() => {
                                    setUserLanguage(UserLanguages.DE);
                                    this.setState({user_language: getUserLanguage()})
                                }
                                }>
                                    {getLabelTextMessage(LabelTextMessageTypes.GERMAN, this.state.user_language)}
                                </NavDropdown.Item>

                                {/* Spanish */}
                                <NavDropdown.Item href="#action3" onClick={() => {
                                    setUserLanguage(UserLanguages.ES);
                                    this.setState({user_language: getUserLanguage()})
                                }
                                }>
                                    {getLabelTextMessage(LabelTextMessageTypes.SPANISH, this.state.user_language)}
                                </NavDropdown.Item>

                                {/* French */}
                                <NavDropdown.Item href="#action3" onClick={() => {
                                    setUserLanguage(UserLanguages.FR);
                                    this.setState({user_language: getUserLanguage()})
                                }
                                }>
                                    {getLabelTextMessage(LabelTextMessageTypes.FRENCH, this.state.user_language)}
                                </NavDropdown.Item>

                            </NavDropdown>
                            <Nav.Link href="https://www.d-fine.com/">
                                {getLabelTextMessage(LabelTextMessageTypes.CONTACTS, this.state.user_language)}
                            </Nav.Link>
                        </Nav>
                        {/* Transaction info: centered horizontally, left of Sign Up, with rectangle styling and vertical centering, grey-blue background */}
                        {transaction && (
                            <div className="transaction-info-box">
                                <span className="transaction-info-name">{transaction.name}</span>
                                <span className="transaction-info-dates">
                                    [{transaction.effective_date} - {transaction.maturity_date}]
                                </span>
                            </div>
                        )}
                        <Nav.Link
                            href="#logout"
                            onClick={async (e) => {
                                e.preventDefault();
                                if (isAuthenticated()) {
                                    try {
                                        await logout();
                                        // Force reload to reflect logged-out state; redirects handled by Keycloak
                                        window.location.href = '/';
                                    } catch (err) {
                                        this.displayMessage({
                                            message_type: FeedbackMessageTypes.ERROR,
                                            message_text: 'Logout failed'
                                        });
                                    }
                                }
                            }}
                            style={{paddingLeft: 10, color: '#003C50', zIndex: 3}}
                        >
                            <MdPersonRemove size={24}/>
                            Logout
                        </Nav.Link>
                    </Navbar.Collapse>
                </Container>
            </Navbar>
        )
    }


    /**
     * Renders the DesignCenterOverView component, passing all required props as defined by BaseViewProps and DesignCenterOverViewProps.
     * Ensures that navigation, model ID resolution, user language, window size, and batch data loading are available to the view.
     * Also passes all transaction-related data entities for use in the design center.
     */
    renderScreenRoutes(): React.JSX.Element {
        const query_string = getQueryString();
        return (
            <Routes>
                <Route path={FrontendURLPaths.LANDING_PAGE}
                       element={<LandingPageOverView
                           key={"LandingPageOverView" + query_string}
                           navigate={this.props.navigate}
                           provideModelIDs={this.provideModelIDs}
                           displayMessage={this.displayMessage}
                           user_language={this.state.user_language}
                           windowInnerHeight={this.state.windowInnerHeight}
                           loadData={this.loadData}
                       />}
                />
                {/* Design Center */}
                <Route path={FrontendURLPaths.DESIGN_CENTER}
                       element={
                           <DesignCenterOverView
                               key={"DesignCenterOverView" + query_string}
                               navigate={this.props.navigate}
                               provideModelIDs={this.provideModelIDs}
                               displayMessage={this.displayMessage}
                               user_language={this.state.user_language}
                               windowInnerHeight={this.state.windowInnerHeight}
                               loadData={this.loadData}
                               transaction={this.state.transaction}
                               variables={this.state.variables}
                               waterfall_items={this.state.waterfall_items}
                               time_series={this.state.time_series}
                               fees={this.state.fees}
                               waterfalls={this.state.waterfalls}
                               formulas={this.state.formulas}
                               triggers={this.state.triggers}
                               portfolios={this.state.portfolios}
                               cash_reserves={this.state.cash_reserves}
                               cash_tranches={this.state.cash_tranches}
                               synthetic_tranches={this.state.synthetic_tranches}
                               synthetic_excess_spreads={this.state.synthetic_excess_spreads}
                               replenishments={this.state.replenishments}
                               transaction_settings={this.state.transaction_settings}
                               chat_history={this.state.chat_history}
                           />
                       }
                />
            </Routes>
        );
    }


    /**
     * Renders the main frame.
     * @returns An React.JSX.Element providing the main frame. Important to keep className pt-5
     */
    render() {
        this.removeExpiredFeedbackMessages();

        return (
            <React.Fragment>
                {this.renderHeaderBar()}

                {this.renderFeedbackMessageToasts()}

                <Container fluid className="pt-5" style={{width: '100vw'}}>
                    {this.renderScreenRoutes()}
                </Container>
            </React.Fragment>
        );
    }
}
